import {
  AfterViewInit,
  Component,
  computed,
  ElementRef,
  inject,
  input,
  model,
  OnInit,
  Optional,
  output,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { GalleryPhoto } from '@capacitor/camera';
import {
  IonButton,
  IonIcon,
  IonFab,
  IonFabButton,
  IonFabList,
  IonAvatar,
  ActionSheetButton,
  ActionSheetController,
  IonSpinner,
} from '@ionic/angular/standalone';
import { timer } from 'rxjs';
import { AuthService } from 'src/app/features/auth/auth.service';
import { ChatFieldComponent } from 'src/app/features/chat/components/chat-field/chat-field.component';
import { MessageContentComponent } from 'src/app/features/chat/components/message-content/message-content.component';
import { ChatService } from 'src/app/features/chat/services/chat.service';
import { SelectImpersonateService } from 'src/app/features/chat/services/select-impersonate.service';
import { CreditsService } from 'src/app/features/credits/credits.service';
import { Message } from 'src/app/features/chat/types/message';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATE_IMPORTS } from 'src/app/features/i18n/translate-imports';

@Component({
  selector: 're-footer-editor',
  imports: [
    IonAvatar,
    IonFabList,
    IonFabButton,
    IonFab,
    IonIcon,
    IonButton,
    MessageContentComponent,
    ChatFieldComponent,
    IonSpinner,
    ...TRANSLATE_IMPORTS,
  ],
  templateUrl: './footer-editor.component.html',
  styleUrls: ['./footer-editor.component.scss'],
})
export class FooterEditorComponent implements AfterViewInit {
  @ViewChild('secondActions') secondActions!: ElementRef<HTMLDivElement>;
  @ViewChild('fabActionsList') fabList!: IonFab;
  text = model<string>();
  addImage = output();
  sendMessage = output();
  removeImage = output();
  quoteMessageHandler = output();

  toggleFab = output();
  openBackdrop = output();
  openModal = output();
  invokeMaster = output();
  invokeLore = output();
  showMasterOverlay = output();

  allowModalEditor = input<boolean>(true);
  allowImpersonate = input<boolean>(true);
  allowImage = input<boolean>(true);
  allowOperation = input<boolean>(true);
  masterBusy = input<boolean>(false);
  showMaster = input<boolean>(true);
  showLore = input<boolean>(false);
  writePlaceholder = input<string | undefined>();
  editorRefreshVersion = input(0);
  imageSelected = input<GalleryPhoto | undefined>();
  quotedMessage = input<Message | undefined>();
  quotables = input<
    {
      id: string;
      displayName: string;
      type: 'user' | 'char';
    }[]
  >();

  impersonateService = inject(SelectImpersonateService, { optional: true });
  private actionSheetController = inject(ActionSheetController);
  private translate = inject(TranslateService);
  private chatService = inject(ChatService);
  private credits = inject(CreditsService);

  profile = toSignal(inject(AuthService).profile$);

  impersonateCharacters = computed(
    () => this.impersonateService?.characters() ?? [],
  );

  hasImpersonateCharacters = computed(
    () => this.impersonateCharacters().length > 0,
  );

  showCharacterPrompt = computed(
    () =>
      this.allowImpersonate() &&
      !this.impersonateService?.impersonate() &&
      this.hasImpersonateCharacters(),
  );

  private buildCharButtons(includeNone: boolean): ActionSheetButton[] {
    const buttons = this.impersonateCharacters().map((char) => ({
      text: char.name,
      handler: () => {
        this.impersonateService?.setImpersonate(char.id);
      },
    }));
    if (includeNone) {
      buttons.push({
        text: this.translate.instant('common.none'),
        handler: () => {
          this.impersonateService?.setImpersonate(undefined);
        },
      });
    }
    return buttons;
  }

  async openCharacterSelection(includeNone = false) {
    const chars = this.impersonateCharacters();
    if (chars.length === 0) return;

    if (!includeNone && chars.length === 1) {
      this.impersonateService?.setImpersonate(chars[0].id);
      return;
    }

    const sheet = await this.actionSheetController.create({
      header: this.translate.instant('chat.impersonate.chooseCharacter'),
      buttons: this.buildCharButtons(includeNone),
    });
    await sheet.present();
  }

  async openMasterAlert() {
    if (this.masterBusy()) {
      this.showMasterOverlay.emit();
      return;
    }
    const confirmed = await this.credits.confirmAiInvoke({
      kind: 'master',
      billed: this.chatService.hasGameplayMasterMessage(),
    });
    if (confirmed) {
      this.invokeMaster.emit();
    }
  }

  async openLoreAlert() {
    if (this.masterBusy()) {
      this.showMasterOverlay.emit();
      return;
    }
    const confirmed = await this.credits.confirmAiInvoke({
      kind: 'lore',
      billed: true,
    });
    if (confirmed) {
      this.invokeLore.emit();
    }
  }

  ngAfterViewInit() {
    timer(1000).subscribe(() => {
      this.secondActions?.nativeElement?.classList?.add('animatedActions');
    });
  }
}
